export type CustomRequestInit = RequestInit & { skip401Redirect?: boolean };

/** HTTP failure with the server response preserved for recoverable API conflicts. */
export class ApiError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(status: number, statusText: string, data: unknown) {
    super(`HTTP ${status}: ${statusText}`);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * /me client-session token — the JWT minted by the magic-link →
 * `/web/auth/verify` exchange for the client onboarding surface.
 *
 * `guided_chat_token` is the LEGACY homebase key. Keep the stores separate:
 * `/me/*` API requests must use `client_session_token`; legacy homebase requests
 * must use `guided_chat_token`.
 */
export const CLIENT_SESSION_TOKEN_KEY = "client_session_token";  // pragma: allowlist secret
export const LEGACY_CLIENT_SESSION_TOKEN_KEY = "guided_chat_token";  // pragma: allowlist secret

/** Retained for legacy homebase AuthContext. */
export const TOKEN_STORAGE_KEY = LEGACY_CLIENT_SESSION_TOKEN_KEY;  // pragma: allowlist secret

/** Read the /me client-session JWT. */
export function getClientSessionToken(): string | null {
  return localStorage.getItem(CLIENT_SESSION_TOKEN_KEY);
}

function isMeApiRequest(url: string): boolean {
  const base =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const pathname = new URL(url, base).pathname;
  return pathname === "/me" || pathname.startsWith("/me/");
}

function getRequestSessionToken(meRequest: boolean): string | null {
  return localStorage.getItem(
    meRequest ? CLIENT_SESSION_TOKEN_KEY : LEGACY_CLIENT_SESSION_TOKEN_KEY
  );
}

export function clearRequestSessionToken(url: string): void {
  localStorage.removeItem(
    isMeApiRequest(url) ? CLIENT_SESSION_TOKEN_KEY : LEGACY_CLIENT_SESSION_TOKEN_KEY
  );
}

function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // Fallback: derive API host from app host (e.g. app.foo.io → api.foo.io)
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('app.')) {
    return `${window.location.protocol}//${window.location.host.replace(/^app\./, 'api.')}`;
  }
  return '';
}

export const API_BASE_URL = getApiBaseUrl();

// Custom fetch instance with auth and error handling
export const customInstance = async <T>(
  url: string,
  options?: CustomRequestInit
): Promise<T> => {
  // Extract skip401Redirect before passing options to fetch
  const skip401Redirect = options?.skip401Redirect;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { skip401Redirect: _skip, ...fetchOptions } = (options ?? {}) as CustomRequestInit;

  // Build full URL
  const fullUrl = `${API_BASE_URL}${url}`
  const meRequest = isMeApiRequest(url)

  // Add the surface-specific auth header. Never fall back between /me and
  // legacy homebase stores; the same browser can legitimately hold both.
  const token = getRequestSessionToken(meRequest)
  const headers = new Headers(fetchOptions.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  // Orval 8 sets Content-Type: multipart/form-data for file uploads, but omits
  // the boundary. Delete it so fetch can auto-set the correct header with boundary.
  if (fetchOptions.body instanceof FormData) {
    headers.delete('Content-Type')
  }

  // Make request
  const response = await fetch(fullUrl, {
    ...fetchOptions,
    headers,
  })

  // Handle 401 errors - force logout and redirect (unless skip401Redirect is set)
  if (response.status === 401 && !skip401Redirect) {
    clearRequestSessionToken(url);
    window.location.href = meRequest ? "/me/auth?expired" : "/auth?expired";
    throw new Error('Unauthorized');
  }

  if (response.status === 401 && skip401Redirect) {
    throw new Error('Unauthorized');
  }

  // Handle all other errors - throw to trigger React Query error state
  if (!response.ok) {
    const contentType = response.headers.get("Content-Type") ?? "";
    const body = await response.text();
    let data: unknown = body || null;
    if (contentType.includes("application/json") && body) {
      try {
        data = JSON.parse(body) as unknown;
      } catch {
        data = body;
      }
    }
    throw new ApiError(response.status, response.statusText, data)
  }

  // Return successful responses in Orval 8 format: {data, status, headers}
  // 204 No Content has no body to parse (e.g. DELETE endpoints)
  const data = response.status === 204 ? undefined : await response.json()
  return {
    data,
    status: response.status,
    headers: response.headers,
  } as T
}

export default customInstance
